import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable, InjectionToken } from "@angular/core";
import { Observable, tap } from "rxjs";
import { DispatchService, ServicePrefix, TimerService } from "../services";

export type ApiEndpoint = `${string}/${string}`;

export type MetricCollector = <T = unknown>(body: T) => Record<string, unknown>;

export const emptyMetricCollector = (_: unknown) =>
{
    return {};
};

@Injectable({ providedIn: 'root' })
export class ApiMonitorService
{
    protected readonly monitors = new Map<ApiEndpoint, MetricCollector>();

    monitorLongApiCalls(serviceMethod: ApiEndpoint, collector: MetricCollector)
    {
        this.monitors.set(serviceMethod, collector);
    }

    isMonitored(serviceMethod: ApiEndpoint): boolean
    {
        return this.monitors.has(serviceMethod);
    }

    getCollector(serviceMethod: ApiEndpoint): MetricCollector
    {
        return this.monitors.get(serviceMethod)
            // This shouldn't be necessary, but it costs little to be safe.
            ?? emptyMetricCollector;
    }
}

/**
 * Defines the limit that the `logLongApiCalls` HttpInterceptorFn uses to determine when to send a log message to the
 * backend.
 * @default 1 MB
 */
export const API_CONTENT_LENGTH_THRESHOLD = new InjectionToken<number>(
    "API_CONTENT_LENGTH_THRESHOLD",
    {
        providedIn: "root",
        factory: () => 1000000 // in bytes
    });

/**
 * Inspects the HttpResponse for any HttpRequests that are for a monitored endpoint according to the
 * `ApiMonitorService`. If the Endpoint isn't being monitored that the request will allowed to fly without any inspect
 * of the HttpResponse.
 *
 * If the endpoint is being monitored, then the HttpResponse will be inspected and a log message generated and sent to
 * the backend if the response has a header for the "ElapsedTime" and the difference between the header value and time-
 * in-flight plus 2 seconds plus an additional second for each kilobyte of the HttpResponse's body.
 *
 * @param req HttpRequest object
 * @param next the next interceptor function in the HttpClient's pipeline
 * @returns
 */
export function logMonitoredApiCalls(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>>
{
    const apiMetricService = inject(ApiMonitorService);
    const timerService = inject(TimerService);
    const dispatchService = inject(DispatchService);
    const contentLengthThreshold = inject(API_CONTENT_LENGTH_THRESHOLD);
    const deliveryTimeThresholdMs = 2000; // 2 seconds;

    const matches = /^\/(?:service(?:pre)?)\/(\w+)\/(\w+)/.exec(req.url);
    if (!matches)
    {
        return next(req);
    }

    const [_, serviceName, methodName] = matches;
    if (serviceName === "ILoggerService"
        || !apiMetricService.isMonitored(`${serviceName}/${methodName}`))
    {
        return next(req);
    }

    const collector = apiMetricService.getCollector(`${serviceName}/${methodName}`);
    const model = req.body;
    const timer = timerService.create();
    timer.start();
    return next(req)
        .pipe(
            tap(inspectResponse));

    function inspectResponse<T>(httpEvent: HttpEvent<T>): void
    {
        if (!(httpEvent instanceof HttpResponse)
            || !httpEvent.headers.has("ElapsedTime"))
        {
            return;
        }

        const elapsed = timer.end();
        const serverElapsed = parseInt(httpEvent.headers.get("ElapsedTime"));
        const elapsedThreshold = serverElapsed + deliveryTimeThresholdMs;
        if (elapsed <= elapsedThreshold)
        {
            return;
        }

        const content = httpEvent.headers.get("Content-Length");
        const contentLength = parseInt(content);

        // log if they took longer than 2 seconds + 1 second per MB
        const contextualThreshold = elapsedThreshold + contentLength / 1000;
        if (contentLength
            && contentLength >= contentLengthThreshold
            && elapsed <= contextualThreshold)
        {
            return;
        }

        dispatchService.dispatch(
            {
                Endpoint: "ILoggerService/LogSlowFrontEndAccess",
                Model: {
                    ServerTime: serverElapsed,
                    ClientTime: elapsed,
                    ServiceName: serviceName,
                    MethodName: methodName,
                    ContentLength: content,
                    ...collector(model),
                }
            })
            // need to trigger the observable to make the call but do not need to do anything with it
            .subscribe();
    }
}
